import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import Modal from '../common/Modal';
import Alert from '../common/Alert';
import Spinner from '../common/Spinner';
import RecoveryCodesDisplay from './RecoveryCodesDisplay';
import MfaService from '../../services/core/MfaService';

const STEP_INTRO = 1;
const STEP_SCAN = 2;
const STEP_VERIFY = 3;
const STEP_CODES = 4;

const SETUP_ERROR_KEYS = {
  MFA_ALREADY_ENABLED: 'profile.mfa.wizard.errors.mfaAlreadyEnabled',
  MFA_LOCAL_PROVIDER_ONLY: 'profile.mfa.wizard.errors.localProviderOnly',
  MFA_DISABLED_GLOBALLY: 'profile.mfa.wizard.errors.mfaDisabledGlobally',
  MFA_RATE_LIMITED: 'profile.mfa.wizard.errors.rateLimited',
};

const ACTIVATE_ERROR_KEYS = {
  INVALID_TOTP: 'profile.mfa.wizard.errors.invalidTotp',
  MFA_SETUP_EXPIRED: 'profile.mfa.wizard.errors.setupExpired',
  MFA_SETUP_NOT_STARTED: 'profile.mfa.wizard.errors.setupNotStarted',
  MFA_ALREADY_ENABLED: 'profile.mfa.wizard.errors.mfaAlreadyEnabled',
  MFA_RATE_LIMITED: 'profile.mfa.wizard.errors.rateLimited',
};

const translateError = (t, message, keyMap) => {
  const key = String(message || '').toUpperCase();
  return t(keyMap[key] || 'profile.mfa.wizard.errors.generic');
};

/**
 * Wizard de 4 pasos para activar MFA (TOTP):
 * 1. Intro -> 2. Escanear QR / clave manual (onSetup) ->
 * 3. Verificar código de 6 dígitos (onActivate) -> 4. Recovery codes.
 *
 * Todo el estado (incluidos los recovery codes en texto plano del paso 4) vive
 * únicamente dentro de este componente: al cerrarse, se descarta con el resto
 * del árbol de React, así que el padre nunca necesita "olvidarlos" a propósito.
 *
 * Por defecto usa MfaService (autoservicio desde "Mi perfil", con sesión ya
 * iniciada). El login con MFA obligatorio por tenant reutiliza este mismo
 * componente pasando `onSetup`/`onActivate` que llaman en su lugar a
 * /api/auth/mfa/setup-required/* (sin sesión: la identidad va en un
 * setupToken) - ver Login.js. En ese caso `onActivate` resuelve además con
 * `accessToken`/`refreshToken`, que este componente ignora pero reenvía tal
 * cual a `onClose` para que el padre pueda usarlos.
 *
 * @param {boolean} isOpen
 * @param {function} onClose - Llamado con `(true, activationResult)` si MFA quedó
 *   activado (el usuario completó el paso 4 y pulsó "guardado"), `(false)` si se
 *   canceló antes de completar la activación.
 * @param {function} [onSetup] - () => Promise<{secret, otpauthUri, issuer, digits, period}>
 * @param {function} [onActivate] - (code) => Promise<{codes, generatedAt, ...}>
 * @param {string} [introTitle] - Override del título del paso 1 (p.ej. para el login forzado).
 * @param {string} [introBody] - Override del cuerpo del paso 1.
 */
const MfaSetupWizard = ({
  isOpen,
  onClose,
  onSetup = () => MfaService.setup(),
  onActivate = (code) => MfaService.activate(code),
  introTitle,
  introBody,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(STEP_INTRO);
  const [setupData, setSetupData] = useState(null);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [setupError, setSetupError] = useState(null);
  const [code, setCode] = useState('');
  const [activateError, setActivateError] = useState(null);
  const [activating, setActivating] = useState(false);
  const [activationResult, setActivationResult] = useState(null);

  const loadSetup = async () => {
    setLoadingSetup(true);
    setSetupError(null);
    try {
      const data = await onSetup();
      setSetupData(data);
    } catch (e) {
      setSetupError(translateError(t, e?.message, SETUP_ERROR_KEYS));
    } finally {
      setLoadingSetup(false);
    }
  };

  const handleStart = () => {
    setStep(STEP_SCAN);
    loadSetup();
  };

  const handleActivate = async (event) => {
    event.preventDefault();
    setActivateError(null);

    const normalizedCode = code.trim();
    if (!normalizedCode) {
      setActivateError(t('profile.mfa.wizard.verify.codeRequired'));
      return;
    }

    setActivating(true);
    try {
      const result = await onActivate(normalizedCode);
      setActivationResult(result);
      setStep(STEP_CODES);
    } catch (e) {
      setActivateError(translateError(t, e?.message, ACTIVATE_ERROR_KEYS));
    } finally {
      setActivating(false);
    }
  };

  const resetState = () => {
    setStep(STEP_INTRO);
    setSetupData(null);
    setLoadingSetup(false);
    setSetupError(null);
    setCode('');
    setActivateError(null);
    setActivating(false);
    setActivationResult(null);
  };

  const handleCancel = () => {
    resetState();
    onClose(false);
  };

  const handleFinish = () => {
    const result = activationResult;
    resetState();
    onClose(true, result);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('profile.mfa.wizard.title')}
      icon={faShieldHalved}
      className="mfa-setup-modal"
    >
      {step === STEP_INTRO && (
        <div className="mfa-wizard-step">
          <h4>{introTitle || t('profile.mfa.wizard.intro.title')}</h4>
          <p>{introBody || t('profile.mfa.wizard.intro.body')}</p>
          <div className="mfa-wizard-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              {t('profile.mfa.wizard.cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleStart}>
              {t('profile.mfa.wizard.intro.start')}
            </button>
          </div>
        </div>
      )}

      {step === STEP_SCAN && (
        <div className="mfa-wizard-step">
          <h4>{t('profile.mfa.wizard.scan.title')}</h4>
          {loadingSetup ? (
            <Spinner message={t('profile.mfa.wizard.scan.loading')} />
          ) : setupError ? (
            <>
              <Alert type="error" message={setupError} onClose={() => setSetupError(null)} />
              <div className="mfa-wizard-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  {t('profile.mfa.wizard.cancel')}
                </button>
                <button type="button" className="btn btn-primary" onClick={loadSetup}>
                  {t('profile.mfa.wizard.scan.retry')}
                </button>
              </div>
            </>
          ) : setupData ? (
            <>
              <p>{t('profile.mfa.wizard.scan.body')}</p>
              <div className="mfa-wizard-qr">
                <QRCodeSVG
                  value={setupData.otpauthUri}
                  size={200}
                  level="M"
                  marginSize={2}
                  title={t('profile.mfa.wizard.scan.title')}
                />
              </div>
              <div className="mfa-wizard-manual">
                <p className="mfa-wizard-manual-title">{t('profile.mfa.wizard.scan.manualTitle')}</p>
                <p>{t('profile.mfa.wizard.scan.manualBody')}</p>
                <code className="mfa-wizard-secret">{setupData.secret}</code>
              </div>
              <div className="mfa-wizard-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  {t('profile.mfa.wizard.cancel')}
                </button>
                <button type="button" className="btn btn-primary" onClick={() => setStep(STEP_VERIFY)}>
                  {t('profile.mfa.wizard.scan.continueButton')}
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {step === STEP_VERIFY && (
        <form className="mfa-wizard-step" onSubmit={handleActivate}>
          <h4>{t('profile.mfa.wizard.verify.title')}</h4>
          <p>{t('profile.mfa.wizard.verify.body')}</p>
          <Alert type="error" message={activateError} onClose={() => setActivateError(null)} />
          <div className="form-group">
            <label htmlFor="mfa-setup-code">{t('profile.mfa.wizard.verify.codeLabel')}</label>
            <input
              id="mfa-setup-code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              className="form-control"
              placeholder={t('profile.mfa.wizard.verify.codePlaceholder')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={activating}
            />
          </div>
          <div className="mfa-wizard-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setStep(STEP_SCAN)}
              disabled={activating}
            >
              {t('profile.mfa.wizard.verify.back')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={activating}>
              {activating ? t('profile.mfa.wizard.verify.activating') : t('profile.mfa.wizard.verify.activate')}
            </button>
          </div>
        </form>
      )}

      {step === STEP_CODES && activationResult && (
        <div className="mfa-wizard-step">
          <h4>{t('profile.mfa.wizard.codes.title')}</h4>
          <RecoveryCodesDisplay
            codes={activationResult.codes}
            generatedAt={activationResult.generatedAt}
            onDone={handleFinish}
          />
        </div>
      )}
    </Modal>
  );
};

export default MfaSetupWizard;
